/*
 * Copyright 2026 Sony Corporation
 */

/** Where the editor's camera was looking from, and at what. */
export interface SavedView {
  alpha: number;
  beta: number;
  radius: number;
  target: { x: number; y: number; z: number };
}

export interface ViewMemory {
  read: (key: string) => SavedView | undefined;
  write: (key: string, view: SavedView) => void;
}

const PREFIX = 'device-verse.view.';

/**
 * A viewpoint is remembered per world *and* per scene: the angle that frames a
 * 0.4 m tabletop is inside the wall of a 12 m room.
 */
export const viewKey = (worldId: string, sceneId: string): string => `${PREFIX}${worldId}::${sceneId}`;

const isVector = (value: unknown): value is SavedView['target'] => {
  const point = value as Record<string, unknown> | null;
  return (
    !!point &&
    typeof point.x === 'number' &&
    typeof point.y === 'number' &&
    typeof point.z === 'number' &&
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    Number.isFinite(point.z)
  );
};

const isSavedView = (value: unknown): value is SavedView => {
  const view = value as Record<string, unknown> | null;
  return (
    !!view &&
    Number.isFinite(view.alpha) &&
    Number.isFinite(view.beta) &&
    Number.isFinite(view.radius) &&
    (view.radius as number) > 0 &&
    isVector(view.target)
  );
};

/**
 * Remembers the viewpoint across a trip back to the world list.
 *
 * Session storage, not the twin: this is one person's vantage point in one tab,
 * not shared state, and a stale entry must never be able to break the editor.
 */
export const createViewMemory = (storage: Storage | undefined = globalThis.sessionStorage): ViewMemory => ({
  read: (key) => {
    try {
      const raw = storage?.getItem(key);
      if (!raw) {
        return undefined;
      }
      const parsed: unknown = JSON.parse(raw);
      return isSavedView(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  },

  write: (key, view) => {
    try {
      storage?.setItem(key, JSON.stringify(view));
    } catch {
      // A full or disabled store costs the user a remembered angle, nothing more.
    }
  },
});
